// src/utils/hbaCallback.js
//
// Handles the HBA authenticator return-trip on the callback tab.
//
// The Authenticator may redirect to either:
//   - `/hba?code=…`    (no hash — when the IDP follows redirect_uri verbatim)
//   - `/#/hba?code=…`  (HashRouter-style — main app uses HashRouter elsewhere)
//
// On a callback URL we:
//   1. Publish { code, state } to the main tab via BroadcastChannel('vzd-auth-code')
//   2. Scrub code/state from history/Referer via history.replaceState
//   3. Replace the document body with a minimal "complete" view
//   4. Schedule window.close()
//
// This runs BEFORE React mounts so the callback tab never paints the SPA shell.

const BROADCAST_CHANNEL_NAME = 'vzd-auth-code';

const completeView = (heading, subtext) =>
  `<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;background:#f4f4f4;color:#333;"><div style="text-align:center;"><strong>${heading}</strong><p style="margin-top:8px;font-size:14px;color:#666;">${subtext}</p></div></div>`;

const COMPLETE_VIEW_HTML = completeView('Authentication complete.', 'You may close this window.');
const NO_CODE_VIEW_HTML = completeView('No authentication code received.', 'You may close this window.');

/**
 * Detect and handle an HBA authenticator return URL.
 *
 * Symmetric route-boundary detection: only matches `#/hba?…` (hash form) or
 * exactly `/hba` (path form). `/hbar`, `/hba-foo`, `#/hba/sub` etc. fall
 * through so the SPA mounts normally.
 *
 * @returns {boolean} true if the URL was a callback (caller MUST skip React
 *   mount), false otherwise (caller should mount the SPA normally).
 *
 * Side effects (only fire when returning true):
 *  - posts `{ code, state }` to BroadcastChannel('vzd-auth-code')
 *  - replaces history (`replaceState(null, '', '/')`) to scrub code/state
 *  - replaces document.body with a minimal "complete" view
 *  - schedules window.close() after 1s
 */
export function handleHbaCallback() {
  const hash = window.location.hash || '';
  // Strict route-boundary match — `startsWith('#/hba?')` won't false-positive on
  // a future `/hbar` or `/hba-foo` route. Mirrors the pathname strict-equality.
  const isHashCallback = hash.startsWith('#/hba?');
  // Require a query string on the path form too — symmetric with the hash form
  // and avoids hijacking a bookmarked/typed `/hba` (no params) into the
  // "complete" view + window.close cycle.
  const isPathCallback = window.location.pathname === '/hba'
    && window.location.search.startsWith('?');
  if (!isHashCallback && !isPathCallback) return false;

  const search = isHashCallback ? hash.slice(hash.indexOf('?')) : window.location.search;
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const state = params.get('state');

  try {
    if (code) {
      const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channel.postMessage({ code, state });
      channel.close();
    }
  } catch {
    // Silently fail — main tab will time out and show an error
  }

  // Scrub code/state from history/Referer.
  try { window.history.replaceState(null, '', '/'); } catch { /* non-fatal */ }

  // Render a minimal complete-view without mounting React, then auto-close.
  // When the URL matched the callback shape but lacked `code` (e.g. malformed
  // redirect, expired flow), say so explicitly so the user isn't told
  // "Authentication complete" when nothing was actually delivered.
  document.body.innerHTML = code ? COMPLETE_VIEW_HTML : NO_CODE_VIEW_HTML;
  setTimeout(() => { try { window.close(); } catch { /* non-fatal */ } }, 1000);
  return true;
}
